import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Match } from "../../types";
import type { TeamPlayer } from "../../../../types";
import LineupBoard, {
  type LineupEditingState,
} from "../../../../components/lineup/LineupBoard";
import {
  findMissingJerseyNumberPlayers,
  type LineupPlayerSelection,
  type LineupZone,
} from "../../../../components/lineup/useLineupDnd";
import QuickCreatePlayerModal from "../../../../components/players/QuickCreatePlayerModal";
import { formatPlayerName } from "../../../../lib/nameFormat";
import {
  fetchMatchLineupSnapshot,
  fetchTeamRoster,
  normalizeLineup,
  patchMatchLineups,
} from "../../lib/lineupSnapshot";
import { ChevronDown, ChevronUp, UserPlus } from "../../../../components/icons";

// EPIC-08 Phase 4 (PMA-COCK-090): pre-match lineup panel mounted directly in
// the cockpit so the analyst never has to leave the logging screen to set
// starters/substitutes. Reuses LineupBoard/QuickCreatePlayerModal as-is
// (Phase 2/3 shared components) — this file owns only the cockpit-side
// wiring: roster/lineup fetch, save, and the untouched-close guard.
//
// PMA-COCK-171: also renders while the match is LIVE, in a bench-only mode
// (starters read-only; only "call up a squad player to the bench" and
// "remove a not-yet-entered substitute" are allowed) — a tester found that a
// match kicked off with an empty bench had no way to substitute at all,
// since this panel used to hard-hide the moment the match left Pending. The
// fetch/normalize/save helpers now live in `lib/lineupSnapshot.ts` so the
// substitution flow's "rest of the squad" list shares the exact same code.

type TeamType = "home" | "away";

function sortRosterByJerseyNumber(roster: TeamPlayer[]): TeamPlayer[] {
  return [...roster].sort((left, right) => {
    // `Number(null)` is `0` — a FINITE number — so a null jersey_number
    // (EPIC-95 T5: now a real, reachable state) would sort to the very
    // TOP as if wearing shirt 0 instead of falling to the end with every
    // other not-yet-set value. Check for null/undefined explicitly first.
    const leftJersey =
      left.jersey_number == null ? NaN : Number(left.jersey_number);
    const rightJersey =
      right.jersey_number == null ? NaN : Number(right.jersey_number);
    const leftSortable = Number.isFinite(leftJersey)
      ? leftJersey
      : Number.MAX_SAFE_INTEGER;
    const rightSortable = Number.isFinite(rightJersey)
      ? rightJersey
      : Number.MAX_SAFE_INTEGER;
    if (leftSortable !== rightSortable) {
      return leftSortable - rightSortable;
    }
    return String(left.player_name || left.player_id).localeCompare(
      String(right.player_name || right.player_id),
    );
  });
}

const filterActivePlayers = (roster: TeamPlayer[]) =>
  roster.filter(
    (p) => (p as unknown as { is_active?: boolean }).is_active !== false,
  );

// PMA-COCK-171: statuses in which the match has kicked off but a bench edit
// (call up a squad player, remove a not-yet-entered substitute) is still
// meaningful. Excludes Completed/Fulltime/Abandoned, where the roster is
// frozen for good.
const LIVE_BENCH_STATUSES: ReadonlySet<Match["status"]> = new Set([
  "Live",
  "Live_First_Half",
  "Live_Second_Half",
  "Halftime",
  "Live_Extra_First",
  "Extra_Halftime",
  "Live_Extra_Second",
  "Penalties",
]);

interface PreMatchLineupPanelProps {
  match: Match;
  t: (...args: any[]) => any;
  fetchMatch: () => Promise<void>;
  currentStatusNormalized: Match["status"];
  /** PMA-COCK-171: who is currently on the field, per team — used only in
   * live-bench mode to lock a substitute who has already entered (they
   * can't be pulled off the matchday squad while playing). Omit pre-match,
   * where nobody has entered yet. */
  onFieldIds?: { home: Set<string>; away: Set<string> };
}

export default function PreMatchLineupPanel({
  match,
  t,
  fetchMatch,
  currentStatusNormalized,
  onFieldIds,
}: PreMatchLineupPanelProps) {
  // `LineupBoard` (+ StartersArea/BenchGrid/NotInSquadZone/PlayerChip) was
  // built for the admin wizard (`MatchesManager.tsx`, itself `admin`-
  // namespace) and every one of its own `t()` calls — `lineupBoard.*` plus
  // several bare keys like `editLineupPlayer`/`noPlayers` — lives only in
  // `admin.json`. This panel's own `t` prop is the cockpit's `logger`-
  // namespace-only `t` (`CockpitContext.tsx`'s `useTranslation("logger")`,
  // no `fallbackNS`), so passing it straight into `LineupBoard` silently
  // fell back to hardcoded English `defaultValue`s (or the raw key, for
  // calls with none) regardless of app language. Bind a second,
  // admin-namespace `t` here — scoped to this file, no change to the
  // cockpit's own namespace, no key duplication into `logger.json` — and
  // use it ONLY for the `<LineupBoard>` subtree below; this panel's own
  // strings (`lineupPanel*`) keep using the `t` prop as before.
  const { t: tLineupBoard } = useTranslation("admin");
  const [isOpen, setIsOpen] = useState(false);
  const [activeTeam, setActiveTeam] = useState<TeamType>("home");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // OQ-EPIC08-03 fix: match.id (from the cockpit's normalizeMatchPayload) is
  // the app-level match_id string, not the Mongo _id the lineups PATCH route
  // requires. Capture the real _id from the CRUD-router snapshot fetched
  // below and use it as the save path param instead.
  const [lineupSnapshotId, setLineupSnapshotId] = useState<string | null>(null);

  const [homeRoster, setHomeRoster] = useState<TeamPlayer[]>([]);
  const [awayRoster, setAwayRoster] = useState<TeamPlayer[]>([]);
  const [homeLineup, setHomeLineup] = useState<LineupPlayerSelection[]>([]);
  const [awayLineup, setAwayLineup] = useState<LineupPlayerSelection[]>([]);
  const [editingPlayer, setEditingPlayer] = useState<LineupEditingState | null>(
    null,
  );
  const [quickCreateTeamType, setQuickCreateTeamType] =
    useState<TeamType | null>(null);

  // Ratified design: the panel is openable while the match is Pending (full
  // edit) or, since PMA-COCK-171, while it is live (bench-only edit) — hard-
  // hidden only once the roster is frozen for good (Completed/Fulltime/
  // Abandoned), not just read-only.
  const isPending =
    currentStatusNormalized === "Pending" ||
    currentStatusNormalized === "Scheduled";
  const isLiveBench = LIVE_BENCH_STATUSES.has(currentStatusNormalized);

  if (!isPending && !isLiveBench) {
    return null;
  }

  const homeTeamId = match.home_team.team_id || match.home_team.id;
  const awayTeamId = match.away_team.team_id || match.away_team.id;

  const teamName = (teamType: TeamType): string =>
    (teamType === "home" ? match.home_team.name : match.away_team.name) ||
    teamType;

  const rosterFor = (teamType: TeamType): TeamPlayer[] =>
    sortRosterByJerseyNumber(
      filterActivePlayers(teamType === "home" ? homeRoster : awayRoster),
    );
  const lineupFor = (teamType: TeamType): LineupPlayerSelection[] =>
    teamType === "home" ? homeLineup : awayLineup;
  const setLineupFor = (teamType: TeamType) =>
    teamType === "home" ? setHomeLineup : setAwayLineup;

  const loadPanelData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [homeRosterData, awayRosterData, snapshot] = await Promise.all([
        homeTeamId ? fetchTeamRoster(homeTeamId) : Promise.resolve([]),
        awayTeamId ? fetchTeamRoster(awayTeamId) : Promise.resolve([]),
        fetchMatchLineupSnapshot(match.id),
      ]);
      const homeLineupData = normalizeLineup(snapshot.home_team?.lineup);
      const awayLineupData = normalizeLineup(snapshot.away_team?.lineup);

      setLineupSnapshotId(snapshot._id ?? null);
      setHomeRoster(homeRosterData);
      setAwayRoster(awayRosterData);
      // No excluded-id seeding any more: a roster player with no entry in
      // the saved lineup renders in the not-in-squad zone because that is
      // exactly what "no lineup entry" now means.
      setHomeLineup(homeLineupData);
      setAwayLineup(awayLineupData);
      setIsDirty(false);
    } catch (err: any) {
      setLoadError(
        err?.response?.data?.detail ||
          err?.message ||
          t(
            "lineupPanelLoadError",
            "Unable to load lineup data. Please try again.",
          ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setSaveError(null);
    setSaveSuccess(false);
    loadPanelData();
  };

  const requestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
      return;
    }
    setIsOpen(false);
  };

  const cancelClose = () => setShowCloseConfirm(false);

  const confirmDiscardClose = () => {
    setShowCloseConfirm(false);
    setIsOpen(false);
  };

  const handleLineupChange = (
    teamType: TeamType,
    lineup: LineupPlayerSelection[],
  ) => {
    setLineupFor(teamType)(lineup);
    setIsDirty(true);
  };

  const startEditPlayer = (
    teamType: TeamType,
    column: "starters" | "substitutes" | "excluded",
    player: TeamPlayer,
  ) => {
    setEditingPlayer({
      teamType,
      playerId: player.player_id,
      column,
      // `?? 0` only widens for TeamPlayer's now-nullable jersey_number
      // (EPIC-95 T5) — the inline jersey editor already lets the admin type
      // a real one in from here, same as for any other player.
      jersey_number: player.jersey_number ?? 0,
      player_name: player.player_name || player.player_id,
    });
  };

  const cancelEditPlayer = () => setEditingPlayer(null);

  const saveEditPlayer = () => {
    if (!editingPlayer) return;
    const { teamType, playerId, jersey_number, player_name } = editingPlayer;
    const trimmedName = player_name.trim();
    if (!trimmedName) return;

    const setRoster = teamType === "home" ? setHomeRoster : setAwayRoster;
    setRoster((prev) =>
      prev.map((p) =>
        p.player_id === playerId
          ? { ...p, jersey_number, player_name: trimmedName }
          : p,
      ),
    );

    setLineupFor(teamType)((prev) => {
      const idx = prev.findIndex((p) => p.player_id === playerId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        jersey_number,
        player_name: formatPlayerName(trimmedName),
      };
      return updated;
    });

    setEditingPlayer(null);
    setIsDirty(true);
  };

  const handleQuickCreated = async (teamType: TeamType) => {
    const teamId = teamType === "home" ? homeTeamId : awayTeamId;
    if (!teamId) return;
    const roster = await fetchTeamRoster(teamId);
    if (teamType === "home") {
      setHomeRoster(roster);
    } else {
      setAwayRoster(roster);
    }
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!lineupSnapshotId) {
      setSaveError(
        t(
          "lineupPanelSaveError",
          "Unable to save the lineup. Please try again.",
        ),
      );
      return;
    }
    // EPIC-95 T5 (lead's revised design): the board lets a null-jersey squad
    // player be called up to starters/bench (see useLineupDnd.ts's
    // `resolveLineupAssignment` — no longer blocked at assign time), so
    // this is the save-time gate, naming every still-missing player at
    // once. The board's own inline jersey/name editor (edit-pencil on any
    // placed chip) is how the admin resolves it before retrying.
    const missingJerseyPlayers = [
      ...findMissingJerseyNumberPlayers(homeLineup),
      ...findMissingJerseyNumberPlayers(awayLineup),
    ];
    if (missingJerseyPlayers.length > 0) {
      const names = missingJerseyPlayers
        .map((p) => p.player_name || p.player_id)
        .join(", ");
      setSaveError(
        t("lineupPanelMissingJerseyNumbers", {
          defaultValue: "Add a jersey number before saving: {{names}}",
        }).replace("{{names}}", names),
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await patchMatchLineups(lineupSnapshotId, homeLineup, awayLineup);
      setIsDirty(false);
      setSaveSuccess(true);
      await fetchMatch();
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.detail ||
          err?.message ||
          t(
            "lineupPanelSaveError",
            "Unable to save the lineup. Please try again.",
          ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-700/60 bg-slate-800/40"
      data-testid="prematch-lineup-panel"
    >
      <button
        type="button"
        onClick={isOpen ? requestClose : handleOpen}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-slate-100"
        data-testid="open-prematch-lineup-panel"
      >
        <span>
          {isLiveBench
            ? t("lineupPanelToggleLive", "Bench (match in progress)")
            : t("lineupPanelToggle", "Pre-match lineup")}
        </span>
        {isOpen ? (
          <ChevronUp size={16} aria-hidden="true" />
        ) : (
          <ChevronDown size={16} aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div className="space-y-4 border-t border-slate-700/60 px-4 py-4">
          {showCloseConfirm && (
            <div
              className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
              data-testid="lineup-panel-close-confirm"
            >
              <span>
                {t(
                  "lineupPanelUnsavedChanges",
                  "You have unsaved lineup changes. Close without saving?",
                )}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancelClose}
                  className="btn btn-sm btn-secondary"
                  data-testid="lineup-panel-close-confirm-cancel"
                >
                  {t("lineupPanelKeepEditing", "Keep editing")}
                </button>
                <button
                  type="button"
                  onClick={confirmDiscardClose}
                  className="btn btn-sm btn-danger"
                  data-testid="lineup-panel-close-confirm-discard"
                >
                  {t("lineupPanelDiscard", "Discard")}
                </button>
              </div>
            </div>
          )}

          {loading && (
            <p
              className="text-sm text-slate-400"
              data-testid="lineup-panel-loading"
            >
              {t("lineupPanelLoading", "Loading roster…")}
            </p>
          )}
          {loadError && (
            <p
              className="text-sm text-red-300"
              data-testid="lineup-panel-load-error"
            >
              {loadError}
            </p>
          )}

          {!loading && !loadError && (
            <>
              <div className="flex gap-2" role="tablist">
                {(["home", "away"] as TeamType[]).map((teamType) => (
                  <button
                    key={teamType}
                    type="button"
                    role="tab"
                    aria-selected={activeTeam === teamType}
                    onClick={() => setActiveTeam(teamType)}
                    className={`btn btn-sm ${
                      activeTeam === teamType ? "btn-primary" : "btn-secondary"
                    }`}
                    data-testid={`prematch-lineup-tab-${teamType}`}
                  >
                    {teamName(teamType)}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between gap-4">
                <h4 className="text-sm font-semibold text-slate-100">
                  {teamName(activeTeam)}
                </h4>
                {!isLiveBench && (
                  <button
                    type="button"
                    onClick={() => setQuickCreateTeamType(activeTeam)}
                    className="btn btn-sm btn-secondary flex shrink-0 items-center gap-2"
                    data-testid={`prematch-lineup-add-player-${activeTeam}`}
                  >
                    <UserPlus className="h-4 w-4" aria-hidden="true" />
                    {t("lineupPanelAddPlayer", "Add new player")}
                  </button>
                )}
              </div>

              <LineupBoard
                teamType={activeTeam}
                candidates={rosterFor(activeTeam)}
                lineup={lineupFor(activeTeam)}
                onLineupChange={(lineup) =>
                  handleLineupChange(activeTeam, lineup)
                }
                readOnly={false}
                benchOnly={isLiveBench}
                benchLockedPlayerIds={
                  isLiveBench ? onFieldIds?.[activeTeam] : undefined
                }
                editingPlayer={editingPlayer}
                onStartEdit={(teamType, player, zone: LineupZone) =>
                  startEditPlayer(
                    teamType,
                    zone === "bench" ? "substitutes" : zone,
                    player,
                  )
                }
                onCancelEdit={cancelEditPlayer}
                onSaveEdit={saveEditPlayer}
                onEditJerseyChange={(jersey_number) =>
                  setEditingPlayer((prev) =>
                    prev ? { ...prev, jersey_number } : prev,
                  )
                }
                onEditNameChange={(player_name) =>
                  setEditingPlayer((prev) =>
                    prev ? { ...prev, player_name } : prev,
                  )
                }
                onOpenQuickCreate={
                  isLiveBench
                    ? undefined
                    : () => setQuickCreateTeamType(activeTeam)
                }
                // admin-namespace `t`, not the cockpit's own logger-namespace
                // prop — see the note above the component body. LineupBoard
                // types `t` as i18next's TFunction; `tLineupBoard` already is
                // one (from `useTranslation("admin")`), no cast needed.
                t={tLineupBoard}
              />

              {saveError && (
                <p
                  className="text-sm text-red-300"
                  data-testid="lineup-panel-save-error"
                >
                  {saveError}
                </p>
              )}
              {saveSuccess && !isDirty && (
                <p
                  className="text-sm text-emerald-300"
                  data-testid="lineup-panel-save-success"
                >
                  {t("lineupPanelSaveSuccess", "Lineup saved.")}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={requestClose}
                  className="btn btn-sm btn-secondary"
                  disabled={saving}
                  data-testid="prematch-lineup-close"
                >
                  {t("lineupPanelClose", "Close")}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn btn-sm btn-primary"
                  disabled={saving || !isDirty}
                  data-testid="prematch-lineup-save"
                >
                  {saving
                    ? t("lineupPanelSaving", "Saving…")
                    : t("lineupPanelSave", "Save lineup")}
                </button>
              </div>
            </>
          )}

          <QuickCreatePlayerModal
            teamId={
              (quickCreateTeamType === "home" ? homeTeamId : awayTeamId) || ""
            }
            teamName={
              quickCreateTeamType ? teamName(quickCreateTeamType) : undefined
            }
            isOpen={Boolean(quickCreateTeamType)}
            onClose={() => setQuickCreateTeamType(null)}
            onCreated={() =>
              quickCreateTeamType
                ? handleQuickCreated(quickCreateTeamType)
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
